import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Icon,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Image,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { HiMail } from "react-icons/hi";
import { RiLockFill } from "react-icons/ri";

import { MobileHeader } from "../components/MobileHeader";
import { loginApi, emailCheckApi } from "../services/auth";
import Intro2 from "../assets/images/intro2.gif";

export const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, returnSlug, returnTo } = location.state || {};

  const toast = useToast();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);

  const calculatedHeight = `calc(100vh - 72px)`;

  const handleSubmit = async () => {
    setLoading(true);

    const emailCheckResponse = await emailCheckApi(email);

    const { exist, scopeTypeMatched } = emailCheckResponse?.data;

    if (exist && scopeTypeMatched) {
      const res = await loginApi({
        email,
        password,
      });

      if (res.status === 200) {
        const expirationDate = new Date();
        expirationDate.setSeconds(expirationDate.getSeconds() + 86400);

        const cookieValue = `book-d booking token=${
          res.token
        }; expires=${expirationDate.toUTCString()}; path=/`;
        document.cookie = cookieValue;

        toast({
          title: "Success",
          description: res.message,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });

        if (returnTo) navigate(returnTo, { replace: true });
        else if (returnSlug) navigate(`/profile/${returnSlug}`);
        else if (cart)
          navigate("/schedule", {
            state: {
              name: res.data.full_name,
              cart,
            },
          });
      } else {
        toast({
          title: "An error occcured",
          description: res.message,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    } else {
      toast({
        title: "An error occcured",
        description: "Email does not exist as a customer account",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }

    setLoading(false);
  };

  return (
    <Box>
      <MobileHeader title="Log In" />

      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        h="72px"
        px="10%"
        align={"center"}
        justify={"space-between"}
        display={{ base: "none", md: "flex" }}
      >
        <Icon fontSize={24} color={"#19202D"} onClick={() => navigate(-1)}>
          <IoIosArrowBack />
        </Icon>

        <Button
          bg={"#FF8E3C"}
          _hover={{ bg: "#FF8E3C" }}
          borderRadius={"12px"}
          color={"white"}
          h="48px"
          fontSize={"15px"}
          lineHeight={"18.2px"}
          onClick={() => navigate("/signup")}
          fontFamily={"RedHatDisplay-Bold"}
        >
          Signup
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }}>
        <Flex
          bg={"linear-gradient(to bottom right, #FFF7F3, #D8F1F2)"}
          h={{ base: "auto", md: calculatedHeight }}
          borderBottomLeftRadius={{ base: 24, md: 0 }}
          borderBottomRightRadius={{ base: 24, md: 0 }}
          px={{ base: "24px", md: "10%" }}
          pt={{ base: "24px", md: 0 }}
          flexDir={"column"}
          justify={"center"}
          display={{ base: "none", md: "flex" }}
        >
          <Flex
            justify={"center"}
            align={"center"}
            h={"100%"}
            display={{ base: "none", md: "flex" }}
          >
            <Image src={Intro2} alt="bookd-intro" objectFit={"cover"} />
          </Flex>
        </Flex>

        <Flex
          pt={{ base: "24px", md: 0 }}
          flexDir={"column"}
          justify={"center"}
          px={{ base: "20px", md: "10%" }}
        >
          <Heading
            fontSize={"32px"}
            color={"#19202D"}
            textAlign={"center"}
            fontFamily={"RedHatDisplay-Bold"}
            display={{ base: "none", md: "block" }}
          >
            Log In
          </Heading>

          <Text
            color={"#4F4F51"}
            fontSize={{ base: "14px", md: "20px" }}
            textAlign={"center"}
            mt={"7px"}
            px="10%"
            display={{ base: "none", md: "block" }}
            mb="32px"
          >
            Log in to book and manage your appointments
          </Text>

          <Box mb="20px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Email
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={24} color={"#9BA3A3"}>
                  <HiMail />
                </Icon>
              </InputLeftElement>

              <Input
                placeholder="Enter Your Email"
                type="email"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                borderColor={"transparent"}
                _focusVisible={{ borderColor: "none" }}
              />
            </InputGroup>
          </Box>

          <Box mb="10px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Password
            </Heading>

            <InputGroup
              borderRadius={"40px"}
              h="56px"
              alignItems={"center"}
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            >
              <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                <Icon fontSize={24} color={"#9BA3A3"}>
                  <RiLockFill />
                </Icon>
              </InputLeftElement>

              <Input
                type="password"
                placeholder="******"
                borderWidth={0}
                fontSize={"16px"}
                ml="8px"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                _focusVisible={{ borderColor: "none" }}
              />
            </InputGroup>
          </Box>

          <Text fontSize={"16px"} color={"#FF762C"}>
            Forgot your password?
          </Text>

          <Button
            bg={"#FF8E3C"}
            _hover={{ bg: "#FF8E3C" }}
            mt="95px"
            mb="76px"
            borderRadius={"30px"}
            color={"white"}
            h="58px"
            fontSize={"17px"}
            fontFamily={"RedHatDisplay-Bold"}
            onClick={handleSubmit}
            mx={{ base: 0, md: "15%" }}
            disabled={loading}
            isLoading={loading}
            loadingText={"Please Wait"}
          >
            Login
          </Button>

          {/* <Text
          fontSize={"16px"}
          fontWeight={500}
          textAlign={"center"}
          color={"#4F4F51"}
        >
          Or continue with
        </Text> */}
        </Flex>
      </SimpleGrid>
    </Box>
  );
};
